/**
 * Advisory (non-blocking) warning for long trips: flags a single-leg order,
 * or each leg of a multi-leg order, whose span from earliest ready time to
 * latest requested delivery time exceeds 13 hours. Not HOS enforcement.
 */
export const DURATION_WARNING_THRESHOLD_HOURS = 13;

export interface DurationStop {
  ready_time: string;
  requested_delivery_time: string;
  requested_delivery_time_end?: string;
  ship_from_street_address: string;
  ship_from_city: string;
  ship_to_street_address: string;
  ship_to_city: string;
}

export interface DurationWarning {
  /** 1-based leg number, or null for a single-leg order. */
  legNumber: number | null;
  hours: number;
}

/** Converts a naive local stop time to a UTC ISO string (or null). */
export type ToUtc = (local: string, street: string, city: string) => string | null;

export function stopDurationHours(stop: DurationStop, toUtc: ToUtc): number | null {
  const endLocal = stop.requested_delivery_time_end || stop.requested_delivery_time;
  if (!stop.ready_time || !endLocal) return null;
  const start = toUtc(stop.ready_time, stop.ship_from_street_address, stop.ship_from_city);
  const end = toUtc(endLocal, stop.ship_to_street_address, stop.ship_to_city);
  if (!start || !end) return null;
  const ms = Date.parse(end) - Date.parse(start);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return ms / 3_600_000;
}

export function computeDurationWarnings(
  form: DurationStop & { order_type?: string; legs?: DurationStop[] },
  toUtc: ToUtc,
): DurationWarning[] {
  const multi = form.order_type === "multi-leg" && (form.legs?.length ?? 0) > 1;
  const stops: Array<{ legNumber: number | null; stop: DurationStop }> = multi
    ? form.legs!.map((stop, i) => ({ legNumber: i + 1, stop }))
    : // Single-leg: the stops editor writes the top-level fields live; legs[0]
      // can be a stale snapshot until save, so always read the form itself.
      [{ legNumber: null, stop: form }];
  const out: DurationWarning[] = [];
  for (const { legNumber, stop } of stops) {
    const hours = stopDurationHours(stop, toUtc);
    if (hours != null && hours > DURATION_WARNING_THRESHOLD_HOURS) out.push({ legNumber, hours });
  }
  return out;
}

export function formatDurationWarning(w: DurationWarning): string {
  const h = w.hours.toFixed(1);
  const who = w.legNumber == null ? "Trip" : `Leg ${w.legNumber}`;
  return `${who} duration is ${h} hours, which exceeds the truck's ${DURATION_WARNING_THRESHOLD_HOURS}-hour logging disk capacity.`;
}
